import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Capacitor } from '@capacitor/core';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { ToasterGlass } from '@/components/ui/use-toast';

import Login from './components/pages/Login';
import Profiles from './components/pages/Profiles';
import SetNewPassword from './components/pages/SetNewPassword';
import AboutUs from './components/pages/AboutUs';
import ContactUs from './components/pages/ContactUs';
import TermsAndConditions from './components/pages/TermsAndConditions';
import Pricing from './components/pages/Pricing';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import CompanyDetail from '@/components/pages/CompanyDetail';
import RegisterClient from '@/components/pages/RegisterClient';
import CompanyRegister from '@/components/pages/CompanyRegister';
import CookiesPolicy from '@/components/pages/CookiesPolicy';
import FAQ from '@/components/pages/FAQ';
import CategoryCityPage from '@/components/pages/CategoryCityPage';
import CategoryPage from '@/components/pages/CategoryPage';
import NotFound from '@/components/pages/NotFound';
import ChooseProfile from '@/components/pages/ChooseProfile';
import DeleteProfile from './components/pages/DeleteProfile';

import Layout from './components/pages/company/Layout';
import Dashboard from './components/pages/company/dashboard/Dashboard';
import Services from './components/pages/company/Services';
import Bonus from './components/pages/company/Bonus';
import Absences from './components/pages/company/Absences';
import Workers from './components/pages/company/Workers';
import CompanyProfile from './components/pages/company/CompanyProfile';
import CompanySettings from './components/pages/company/CompanySettings';
import Calendar from './components/pages/company/Calendar';
import Appointments from './components/pages/company/Appointments';
import BrandingAndLinks from './components/pages/company/BrandingAndLinks';
import SubsCriptions from './components/pages/company/Subscriptions';
import Products from './components/pages/company/Products';
import Expenses from './components/pages/company/Expenses';
import PlanRequerido from './components/pages/shared/PlanRequerido';

import LayoutWorker from './components/pages/worker/LayoutWorker';
import DashboardWorker from './components/pages/worker/DashboardWorker';
import WorkerCalendar from './components/pages/worker/WorkerCalendar';
import DisplayQr from './components/pages/worker/DisplayQR';
import WorkerProfile from './components/pages/worker/WorkerProfile';
import WorkerAppointments from './components/pages/worker/WorkerAppointments';

import ClientBooking from './components/pages/client/ClientBooking';
import CancelBooking from './components/pages/client/CancelBooking';
import DashboardClient from './components/pages/client/DashboardClient';
import LayoutClient from './components/pages/client/LayoutClient';
import ClientProfile from './components/pages/client/ClientProfile';
import ClientAppointments from './components/pages/client/ClientAppointments';
import ClientBonus from './components/pages/client/ClientBonus';
import ExploreCompanies from './components/pages/client/ExploreCompanies';
import ReactivateProfile from './components/pages/client/ReactivateProfile';

import Clients from './components/pages/shared/Clients';
import Blacklist from './components/pages/shared/Blacklist';
import PurchasedVouchers from './components/pages/shared/PurchasedVouchers';
import RateAppointment from './components/pages/shared/RateAppointment';

import LayoutAdmin from './components/pages/admin/LayoutAdmin';
import DashboardAdmin from './components/pages/admin/DashboardAdmin';

import OAuthCallback from './components/utils/OAuthCallback';
import DeepLinkHandler from './components/utils/DeepLinkHandler';
import PrivateRoute from '@/auth/PrivateRoute';
import RequierePlan from '@/auth/RequierePlan';
import RequiereAccesoTrabajador from '@/auth/RequiereAccesoTrabajador';
import { guardarEleccionCookies } from '@/lib/consentimientoCookies';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Resetea el scroll al inicio de la página en cada cambio de ruta
// (necesario porque React Router no lo hace por defecto).
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  const { t } = useTranslation();
  const [cookiesChoice, setCookiesChoice] = useState(
    localStorage.getItem('cookiesChoice') || ''
  );

  const handleAcceptCookies = () => {
    guardarEleccionCookies('accepted');
    setCookiesChoice('accepted');
  };

  const handleRejectCookies = () => {
    guardarEleccionCookies('rejected');
    setCookiesChoice('rejected');
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <DeepLinkHandler />
      <SentryRoutes>
        {/* 🔓 Rutas públicas */}
        <Route path="/" element={<Login />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/client-booking/:id_empresa" element={<ClientBooking />} />
        <Route path="/cancelar-cita" element={<CancelBooking />} />
        <Route path="/set-new-password" element={<SetNewPassword />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/delete-profile" element={<DeleteProfile />} />
        <Route path="/:categoria_slug" element={<CategoryPage />} />
        <Route
          path="/:categoria_slug/:ciudad_slug"
          element={<CategoryCityPage />}
        />
        <Route
          path="/:categoria_slug/:ciudad_slug/:empresa_slug"
          element={<CompanyDetail />}
        />
        <Route path="/valorar-cita/:id_cita" element={<RateAppointment />} />
        <Route path="/register-client" element={<RegisterClient />} />
        <Route path="/register-company" element={<CompanyRegister />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/cookies" element={<CookiesPolicy />} />
        <Route path="/explorar" element={<ExploreCompanies />} />
        <Route path="/choose-profile" element={<ChooseProfile />} />

        {/* 🔒 Rutas privadas */}
        <Route element={<PrivateRoute />}>
          <Route path="/profiles" element={<Profiles />} />
          <Route path="/reactivar-perfil" element={<ReactivateProfile />} />

          {/* Panel Cliente */}
          <Route path="/panel-cliente" element={<LayoutClient />}>
            <Route index element={<Navigate to="explore" replace />} />
            <Route path="dashboard" element={<DashboardClient />} />
            <Route path="profile" element={<ClientProfile />} />
            <Route path="appointments" element={<ClientAppointments />} />
            <Route path="bonus" element={<ClientBonus />} />
            <Route path="explore" element={<ExploreCompanies />} />
          </Route>

          {/* Panel Empresa */}
          <Route path="/panel-empresa" element={<Layout />}>
            {/* Siempre accesibles (aunque no haya plan): suscripción,
                perfil, ajustes y la propia pantalla de plan requerido —
                para que quien no tenga plan pueda entrar a contratarlo. */}
            <Route path="subscriptions" element={<SubsCriptions />} />
            <Route path="companyProfile" element={<CompanyProfile />} />
            <Route path="companySettings" element={<CompanySettings />} />
            <Route path="plan-requerido" element={<PlanRequerido />} />

            {/* Operativa: requiere plan efectivo activo. Sin él, el guard
                redirige a /panel-empresa/plan-requerido. */}
            <Route element={<RequierePlan />}>
              <Route index element={<Dashboard />} />
              <Route path="services" element={<Services />} />
              <Route path="products" element={<Products />} />
              <Route path="expenses" element={<Expenses />} />
              <Route path="bonus" element={<Bonus />} />
              <Route path="clients" element={<Clients />} />
              <Route path="workers" element={<Workers />} />
              <Route path="absences" element={<Absences />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="purchasedVouchers" element={<PurchasedVouchers />} />
              <Route path="branding" element={<BrandingAndLinks />} />
              <Route path="blacklist" element={<Blacklist />} />
            </Route>
          </Route>

          {/* Panel Trabajador — requiere acceso (empresa con plan + el
              trabajador con acceso). Sin él, el guard redirige a /profiles. */}
          <Route element={<RequiereAccesoTrabajador />}>
            <Route path="/panel-trabajador" element={<LayoutWorker />}>
              <Route index element={<DashboardWorker />} />
              <Route path="workerCalendar" element={<WorkerCalendar />} />
              <Route path="displayQR" element={<DisplayQr />} />
              <Route path="blacklist" element={<Blacklist />} />
              <Route path="clients" element={<Clients />} />
              <Route path="purchasedVouchers" element={<PurchasedVouchers />} />
              <Route path="workerProfile" element={<WorkerProfile />} />
              <Route path="workerAppointments" element={<WorkerAppointments />} />
            </Route>
          </Route>

          {/* Panel Admin */}
          <Route path="/panel-admin" element={<LayoutAdmin />}>
            <Route index element={<DashboardAdmin />} />
          </Route>
        </Route>

        {/* 🚨 404 — noindex para URLs inexistentes */}
        <Route path="*" element={<NotFound />} />
      </SentryRoutes>

      {/* ═══════════════ COOKIES BANNER ═══════════════ */}
      {/* Oculto en la app nativa (Capacitor): no aplica consentimiento web de cookies */}
      {!cookiesChoice && !Capacitor.isNativePlatform() && (
        <motion.div
          className="fixed bottom-0 left-0 w-full text-white text-sm p-4 flex flex-col md:flex-row items-center justify-between z-50 shadow-lg"
          style={{
            background:
              'linear-gradient(135deg, #103254 0%, #1a4a6e 55%, #3d7fa0 100%)',
          }}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="mb-3 md:mb-0 md:mr-4 text-center md:text-left text-white/90">
            {t('cookies.text')}{' '}
            <Link
              to="/cookies"
              className="underline underline-offset-2 hover:text-white transition-colors"
            >
              {t('cookies.policy')}
            </Link>
          </p>
          <div className="flex gap-2">
            <Button
              className="bg-white/10 border border-white/30 text-white hover:bg-white/20 backdrop-blur-sm"
              onClick={handleRejectCookies}
            >
              {t('cookies.reject')}
            </Button>
            <Button
              className="bg-white text-[#103254] hover:bg-gray-100"
              onClick={handleAcceptCookies}
            >
              {t('cookies.accept')}
            </Button>
          </div>
        </motion.div>
      )}

      <ToasterGlass />
    </BrowserRouter>
  );
}

export default App;